import { Button } from '@/components/ui/button';
import { ORDER_TYPE, token } from '@/constants/solana';
import { useFetchTokenBalance } from '@/hooks/solana/query/use-fetch-token-balance';
import { useSolanaBalance } from '@/hooks/solana/query/use-solana-balance';
import { calculateMarketOrderFill, cn, mapOrderbook } from '@/lib/utils';
import { useOrderbookStore } from '@/store/orderbook';

interface Props {
  setTokenAmount: (amount: string) => void;
  tokenAmount: string;
  setShares: (shares: string) => void;
  orderbookData: ReturnType<typeof mapOrderbook>;
  className?: string;
}

export const EventBuyMarketButtons = ({
  setTokenAmount,
  tokenAmount,
  setShares,
  orderbookData,
  className,
}: Props) => {
  const { isUsdc } = useOrderbookStore();

  const handleAddAmount = (amount: number) => {
    const currentAmount = isNaN(Number(tokenAmount)) ? 0 : Number(tokenAmount);
    const newAmount = Number((currentAmount + amount).toFixed(6));

    setTokenAmount(newAmount.toString());
    const { totalShares } = calculateMarketOrderFill(
      orderbookData,
      newAmount.toString(),
      ORDER_TYPE.BUY,
    );
    setShares(Math.floor(totalShares * 100) / 100 + '');
  };

  const { data: tokenBalance } = useFetchTokenBalance(token);
  const { data: solanaBalance } = useSolanaBalance();

  const balance = isUsdc ? tokenBalance : solanaBalance;

  const handleSetMax = () => {
    // Calculate the token amount to set
    let calculatedAmount = '0';

    if (balance?.uiAmount) {
      // If token is SOL, reserve 0.01 SOL or 2% of amount (whichever is higher) for transaction fees
      const reserveAmount = !isUsdc
        ? Math.max(0.01, balance?.uiAmount ? balance.uiAmount * 0.02 : 0.01)
        : 0;

      // Calculate available amount (with 2 decimal precision)
      const availableAmount = Math.max(0, balance.uiAmount - reserveAmount);
      const roundedAmount = Math.floor(availableAmount * 100) / 100;

      calculatedAmount = roundedAmount.toString();
    }

    setTokenAmount(calculatedAmount);
    const { totalShares } = calculateMarketOrderFill(
      orderbookData,
      calculatedAmount,
      ORDER_TYPE.BUY,
    );
    setShares(Math.floor(totalShares * 100) / 100 + '');
  };

  return (
    <div
      className={cn(
        'flex flex-row flex-wrap justify-end gap-[10px] !text-[14px]',
        className,
      )}
    >
      <Button
        size="medium"
        variant="accent"
        className="flex items-center gap-0.5 text-[14px]"
        onClick={() => handleAddAmount(isUsdc ? 10 : 0.1)}
      >
        {isUsdc ? (
          '+10$'
        ) : (
          <>
            <span>+0.1</span>
            <img src="/icons/sol.svg" alt="SOL" className="size-[16px]" />
          </>
        )}
      </Button>
      <Button
        size="medium"
        variant="accent"
        className={cn(
          isUsdc && 'max-sm:hidden',
          'flex items-center gap-0.5 text-[14px]',
        )}
        onClick={() => handleAddAmount(isUsdc ? 25 : 0.25)}
      >
        {isUsdc ? (
          '+25$'
        ) : (
          <>
            <span>+0.25</span>
            <img src="/icons/sol.svg" alt="SOL" className="size-[16px]" />
          </>
        )}
      </Button>
      {isUsdc && (
        <Button
          size="medium"
          variant="accent"
          className="text-[14px]"
          onClick={() => handleAddAmount(50)}
        >
          +50$
        </Button>
      )}
      <Button
        size="medium"
        variant="accent"
        className="text-[14px]"
        onClick={handleSetMax}
      >
        MAX
      </Button>
    </div>
  );
};
